import { Fragment, useRef } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import { AlertTriangle, Check, Trash2 } from "lucide-react";

import { AlertNote } from "@/components/AlertNote";
import { CountryFlag } from "@/components/CountryFlag";
import { GoogleIcon } from "@/components/icons/GoogleIcon";
import {
  AiModeIcon,
  AiOverviewsIcon,
  ChatGptIcon,
  GeminiIcon,
  PerplexityIcon,
} from "@/components/icons/AiEngineIcons";
import { brand } from "@/theme/theme";
import { useCountryNames } from "./hooks";
import { engineSubtitle, engineTitle, isAiEngine } from "./seoSetupUtils";

export const SUCCESS = "#0F9D58";
export const BORDER = "#E8ECF2";

/** Bordered white card with a title, like each block of the provider's own setup. */
export function SetupCard({ title, description, aside, footer, children, sx }) {
  return (
    <Box sx={{ borderRadius: "12px", border: `1px solid ${BORDER}`, backgroundColor: "#fff", ...sx }}>
      {title && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            px: { xs: 2, sm: 2.5 },
            pt: { xs: 2, sm: 2.25 },
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: brand.ink }}>{title}</Typography>
            {description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.55 }}>
                {description}
              </Typography>
            )}
          </Box>
          {aside}
        </Box>
      )}
      {children && <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>{children}</Box>}
      {footer && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
            px: { xs: 2, sm: 2.5 },
            pb: 2,
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
}

SetupCard.propTypes = {
  title: PropTypes.string,
  description: PropTypes.node,
  aside: PropTypes.node,
  footer: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object,
};

/** "Madrid, Comunidad de Madrid" for a Google location, else the country. */
const engineLocation = (engine, countryNames) => {
  if (engine.display?.location) return engine.display.location;
  if (engine.region_name) return engine.region_name.split(", ").slice(0, -1).join(", ") || engine.region_name;
  return engine.display?.country || countryNames[engine.country] || engine.country;
};

/**
 * Engines as rows of engine, location and language. `leading`/`trailing` render an extra
 * cell per row (checkbox, counter, actions); `below` renders under a row.
 */
export function EngineTable({ engines, errors, leading, trailing, below, header, trailingWidth = 48 }) {
  const countryNames = useCountryNames();
  const columns = [
    leading ? "36px" : null,
    "minmax(0, 1.5fr)",
    "minmax(0, 1.3fr)",
    "64px",
    trailing ? `${trailingWidth}px` : null,
  ]
    .filter(Boolean)
    .join(" ");
  const mobileColumns = [leading ? "36px" : null, "minmax(0, 1fr)", trailing ? "auto" : null].filter(Boolean).join(" ");
  const cellSx = { minWidth: 0, display: "flex", alignItems: "center", gap: 1 };
  const desktopOnly = { display: { xs: "none", sm: "flex" } };

  return (
    <Box sx={{ borderRadius: "10px", border: `1px solid ${BORDER}`, overflow: "hidden" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: mobileColumns, sm: columns },
          alignItems: "center",
          columnGap: 1.5,
          px: 1.5,
          py: 0.75,
          backgroundColor: "#F8FAFC",
          borderBottom: `1px solid ${BORDER}`,
          "& > *": { fontSize: "0.75rem", fontWeight: 600, color: "text.secondary" },
        }}
      >
        {leading && <Box sx={cellSx}>{header?.leading}</Box>}
        <Typography sx={cellSx}>{header?.engine || "Motor de búsqueda"}</Typography>
        <Typography sx={{ ...cellSx, ...desktopOnly }}>Ubicación</Typography>
        <Typography sx={{ ...cellSx, ...desktopOnly }}>Idioma</Typography>
        {trailing && <Typography sx={{ ...cellSx, justifyContent: "flex-end" }}>{header?.trailing}</Typography>}
      </Box>

      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {engines.map((engine, index) => {
          const error = errors?.[engine.key];
          const location = engineLocation(engine, countryNames);
          const language =
            (engine.family === "google" || isAiEngine(engine)) && engine.lang_code ? engine.lang_code.toUpperCase() : "—";
          return (
            <Box component="li" key={engine.key} sx={{ borderTop: index ? "1px solid #F1F5F9" : "none" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: mobileColumns, sm: columns },
                  alignItems: "center",
                  columnGap: 1.5,
                  px: 1.5,
                  py: 1,
                  backgroundColor: "#fff",
                }}
              >
                {leading && <Box sx={cellSx}>{leading(engine)}</Box>}
                <Box sx={{ ...cellSx, gap: 1.25 }}>
                  <EngineIcon family={engine.family} size={28} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {engineTitle(engine)}
                    </Typography>
                    {error ? (
                      <Typography variant="caption" color="error" sx={{ display: "block" }}>
                        {error}
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: { xs: "flex", sm: "none" }, alignItems: "center", gap: 0.75 }}
                      >
                        <CountryFlag code={engine.country} width={14} />
                        {[location, language !== "—" ? language : ""].filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ ...cellSx, ...desktopOnly }}>
                  <CountryFlag code={engine.country} />
                  <Typography variant="body2" noWrap title={engine.region_name || location}>
                    {location}
                  </Typography>
                </Box>
                <Typography variant="body2" color={language === "—" ? "text.disabled" : "text.primary"} sx={desktopOnly}>
                  {language}
                </Typography>
                {trailing && <Box sx={{ ...cellSx, justifyContent: "flex-end" }}>{trailing(engine)}</Box>}
              </Box>
              {below?.(engine)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

EngineTable.propTypes = {
  engines: PropTypes.arrayOf(PropTypes.object).isRequired,
  errors: PropTypes.object,
  leading: PropTypes.func,
  trailing: PropTypes.func,
  below: PropTypes.func,
  header: PropTypes.shape({ leading: PropTypes.node, engine: PropTypes.node, trailing: PropTypes.node }),
  trailingWidth: PropTypes.number,
};

/** Centered icon and text for an empty list. */
export function EmptyState({ icon, title, description }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 1, py: 4, px: 2 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "12px",
          border: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: brand.ink,
          mb: 0.5,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, color: brand.ink }}>{title}</Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          {description}
        </Typography>
      )}
    </Box>
  );
}

EmptyState.propTypes = { icon: PropTypes.node, title: PropTypes.string.isRequired, description: PropTypes.node };

export function StepIntro({ title, description, aside }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: brand.ink }}>{title}</Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {description}
          </Typography>
        )}
      </Box>
      {aside}
    </Box>
  );
}

StepIntro.propTypes = { title: PropTypes.string.isRequired, description: PropTypes.node, aside: PropTypes.node };

export function FieldLabel({ children, htmlFor, hint }) {
  return (
    <Typography
      component="label"
      htmlFor={htmlFor}
      variant="body2"
      sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mb: 1, fontWeight: 500 }}
    >
      {children}
      {hint && (
        <Typography component="span" variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Typography>
  );
}

FieldLabel.propTypes = { children: PropTypes.node, htmlFor: PropTypes.string, hint: PropTypes.node };

/** Messages of a 400 from the final POST, shown at the top of their step. */
export function ServerErrors({ messages }) {
  if (!messages?.length) return null;
  return (
    <AlertNote variant="danger">
      {messages.length === 1 ? (
        messages[0]
      ) : (
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </Box>
      )}
    </AlertNote>
  );
}

ServerErrors.propTypes = { messages: PropTypes.arrayOf(PropTypes.string) };

export function RemoveButton({ label, onClick, tooltip = "Quitar", size = 16 }) {
  return (
    <Tooltip title={tooltip}>
      <IconButton
        size="small"
        aria-label={label}
        onClick={onClick}
        sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
      >
        <Trash2 size={size} />
      </IconButton>
    </Tooltip>
  );
}

RemoveButton.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  tooltip: PropTypes.string,
  size: PropTypes.number,
};

const LINE_HEIGHT = 22;

/** Textarea with a line-number gutter, one entry per line. */
export function NumberedTextarea({ id, value, onChange, placeholder, rows = 6, error = false, ariaLabel }) {
  const gutterRef = useRef(null);
  const lines = Math.max(rows, String(value || "").split("\n").length);
  const height = rows * LINE_HEIGHT + 20;

  return (
    <Box
      sx={{
        display: "flex",
        border: "1px solid",
        borderColor: error ? "#d32f2f" : "divider",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#fff",
        "&:focus-within": { borderColor: error ? "#d32f2f" : "#000" },
      }}
    >
      <Box
        ref={gutterRef}
        aria-hidden
        sx={{
          flexShrink: 0,
          width: 40,
          py: "10px",
          pr: 1,
          height,
          overflow: "hidden",
          backgroundColor: "#F6F7FA",
          borderRight: "1px solid #E8ECF2",
          color: "#94A3B8",
          fontSize: "0.75rem",
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
          userSelect: "none",
        }}
      >
        {Array.from({ length: lines }, (_, index) => (
          <Box key={index} sx={{ height: LINE_HEIGHT, lineHeight: `${LINE_HEIGHT}px` }}>
            {index + 1}
          </Box>
        ))}
      </Box>
      <Box
        component="textarea"
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onScroll={(event) => {
          if (gutterRef.current) gutterRef.current.scrollTop = event.currentTarget.scrollTop;
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        spellCheck={false}
        wrap="off"
        sx={{
          flex: 1,
          minWidth: 0,
          height,
          resize: "none",
          border: "none",
          outline: "none",
          py: "10px",
          px: 1.5,
          font: "inherit",
          fontSize: "0.875rem",
          lineHeight: `${LINE_HEIGHT}px`,
          color: "text.primary",
          backgroundColor: "transparent",
          overflowX: "auto",
          "&::placeholder": { color: "#94A3B8" },
        }}
      />
    </Box>
  );
}

NumberedTextarea.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  rows: PropTypes.number,
  error: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

const LOGOS = {
  google: GoogleIcon,
  google_ai_overview: AiOverviewsIcon,
  google_ai_mode: AiModeIcon,
  chatgpt: ChatGptIcon,
  gemini: GeminiIcon,
  perplexity: PerplexityIcon,
};

const ENGINE_LETTERS = {
  bing: { letter: "b", color: "#008373" },
  yahoo: { letter: "Y!", color: "#6001D2" },
};

/** `bare`: just the logo, without the grey tile. */
export function EngineIcon({ family, size = 32, bare = false }) {
  let content;
  const scale = bare ? 1.5 : 1;
  const Logo = LOGOS[family];
  if (Logo) content = <Logo size={size * 0.55 * scale} />;
  else {
    const meta = ENGINE_LETTERS[family] || { letter: (family || "?").charAt(0).toUpperCase(), color: "#475569" };
    content = (
      <Typography component="span" sx={{ fontWeight: 800, fontSize: size * 0.4 * scale, color: meta.color, lineHeight: 1 }}>
        {meta.letter}
      </Typography>
    );
  }
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "8px",
        backgroundColor: bare ? "transparent" : "#F3F4F6",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {content}
    </Box>
  );
}

EngineIcon.propTypes = { family: PropTypes.string, size: PropTypes.number, bare: PropTypes.bool };

/** Engine title plus its subtitle (or the step error in its place). */
export function EngineLabel({ engine, error, noWrap = false, children }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap={noWrap}>
        {engineTitle(engine)}
      </Typography>
      <Typography
        variant="caption"
        sx={{ display: "block", color: error ? "error.main" : "text.secondary" }}
        noWrap={noWrap}
      >
        {error || engineSubtitle(engine)}
      </Typography>
      {children}
    </Box>
  );
}

EngineLabel.propTypes = {
  engine: PropTypes.object.isRequired,
  error: PropTypes.string,
  noWrap: PropTypes.bool,
  children: PropTypes.node,
};

// Same circles as ContentGenerator/workflow/WorkflowStepper.jsx.
const CIRCLE_STATE_SX = {
  done: { backgroundColor: "#E3F6EA", color: SUCCESS, border: "1px solid #C4E8D2" },
  active: { backgroundColor: brand.inkFill, color: brand.inkFillText, boxShadow: "0 4px 14px rgba(15, 23, 42, 0.22)" },
  error: { backgroundColor: "#FEE2E2", color: "#DC2626", border: "1px solid #FCA5A5" },
  pending: { backgroundColor: "#fff", color: "#64748b", border: "1px solid #CBD5E1" },
};

/** Numbered circles on desktop, "Paso n de N" plus a bar on phones. */
export function SetupStepperHeader({ steps, activeIndex, canSelect, onSelect }) {
  const progress = steps.length > 1 ? (activeIndex / (steps.length - 1)) * 100 : 0;

  return (
    <Box>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 700, color: brand.ink }}>
            {steps[activeIndex]?.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            Paso {activeIndex + 1} de {steps.length}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          aria-label="Progreso de la configuración"
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: "#EEF0F4",
            "& .MuiLinearProgress-bar": { borderRadius: 3, background: brand.gradient },
          }}
        />
      </Box>

      <Box component="nav" aria-label="Pasos de la configuración" sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
        {steps.map((step, index) => {
          const current = index === activeIndex;
          const clickable = !current && canSelect(index);
          return (
            <Fragment key={step.key}>
              {index > 0 && (
                <Box
                  aria-hidden
                  sx={{
                    flex: 1,
                    minWidth: 12,
                    height: 2,
                    mx: 0.75,
                    borderRadius: 1,
                    backgroundColor: index <= activeIndex ? brand.inkFill : "#E2E8F0",
                    transition: "background-color 0.2s",
                  }}
                />
              )}
              <Box
                component={clickable ? "button" : "div"}
                type={clickable ? "button" : undefined}
                onClick={clickable ? () => onSelect(index) : undefined}
                aria-current={current ? "step" : undefined}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 0.5,
                  pr: 1,
                  border: "none",
                  borderRadius: "10px",
                  background: current ? brand.soft : "transparent",
                  cursor: clickable ? "pointer" : "default",
                  font: "inherit",
                  flexShrink: 0,
                  "&:hover": clickable ? { backgroundColor: "#F6F7FA" } : {},
                  "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 2 },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    minWidth: 28,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    transition: "all 0.15s",
                    ...(CIRCLE_STATE_SX[step.state] || CIRCLE_STATE_SX.pending),
                  }}
                >
                  {step.state === "done" ? (
                    <Check size={14} strokeWidth={3} />
                  ) : step.state === "error" ? (
                    <AlertTriangle size={13} />
                  ) : (
                    index + 1
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: current ? 700 : 600,
                    color: current ? brand.ink : "#475569",
                    whiteSpace: "nowrap",
                  }}
                >
                  {step.title}
                </Typography>
              </Box>
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

SetupStepperHeader.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, title: PropTypes.string, state: PropTypes.string }))
    .isRequired,
  activeIndex: PropTypes.number.isRequired,
  canSelect: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};
